import axios, {
  AxiosError,
  AxiosInstance,
  CreateAxiosDefaults,
  InternalAxiosRequestConfig
} from 'axios'
import axiosRetry from 'axios-retry'

import { HeaderName } from '../constants/header-name'
import microAgent from '../functions/create-new-relic-agent'
import { getSessionStoreState } from '../stores/session'

const isTestEnv = process.env.ENVIRONMENT === 'test'

const RETRY_METHODS = ['get', 'head', 'options', 'delete']

export const handleRequestFulfilled = async (
  config: InternalAxiosRequestConfig<CreateAxiosDefaults>,
  shouldVerifyEnv = true
) => {
  if (isTestEnv && shouldVerifyEnv) {
    throw new Error('HTTP requests are not allowed in test environment')
  }

  const {
    authData: { accessToken },
    businessUnit: { currentUnit }
  } = getSessionStoreState()

  config.headers.set(HeaderName.Authorization, `Bearer ${accessToken}`)
  config.headers.set(HeaderName.BusinessUnit, currentUnit.code)

  return config
}

export const handleRequestRejected = (error: Error): Promise<Error> => {
  return Promise.reject(error)
}

export const handleRetryCondition = (error: AxiosError) => {
  const method = error.config?.method ?? ''

  return (
    axiosRetry.isNetworkOrIdempotentRequestError(error) &&
    RETRY_METHODS.includes(method)
  )
}

export interface CreateHttpClientOptions {
  baseURL: string | undefined
}

export const createHttpClient = ({
  baseURL
}: CreateHttpClientOptions): AxiosInstance => {
  // `exactOptionalPropertyTypes` não aceita `baseURL: undefined` explícito —
  // com a env ausente, a chave simplesmente não vai no config.
  const config: CreateAxiosDefaults = baseURL ? { baseURL } : {}
  const instance = axios.create(config)

  instance.interceptors.request.use(
    handleRequestFulfilled,
    handleRequestRejected
  )

  instance.interceptors.response.use(
    resp => resp,
    error => {
      microAgent.noticeError(error, {
        requestUrl: error.config?.url,
        requestMethod: error.config?.method,
        requestStatus: error.response?.status
      })

      return Promise.reject(error)
    }
  )

  axiosRetry(instance, {
    retries: 3,
    retryCondition: handleRetryCondition,
    retryDelay: axiosRetry.exponentialDelay
  })

  return instance
}
